import { Phone, ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
const NAVY = "#081438";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const CREAM = "#FAF7F1";
const CREAM_DEEP = "#F0EBE0";
const INK = "#1A1F2E";
const INK_MUTED = "#5A6478";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
type Pillar = {
  number: string;
  category: string;
  title: string;
  desc: string;
  proof: string;
};
const PILLARS: Pillar[] = [{
  number: "01",
  category: "Panel Stock",
  title: "150+ replacement panels in stock at Bass Hill.",
  desc: "Most garage door tradies tell you the whole door needs replacing because they don't carry panels. We hold 150+ replacement panels — every common B&D, Steel-Line, and Centurion profile in white, primrose, paperbark, and woodland grey. Single panel swap, same-day, half the price of full replacement.",
  proof: "150+ panels · colour matched"
}, {
  number: "02",
  category: "Specialisation",
  title: "Sectional doors are our biggest service line.",
  desc: "We've serviced over 3,600 sectional doors in 15 years — more than any other door type. We know exactly which hinges fail on Panelift Icons, which rollers seize on Centurion Bosses, and which weather seals perish first on Steel-Line. It's not guesswork.",
  proof: "3,600+ sectional doors serviced"
}, {
  number: "03",
  category: "Honesty",
  title: "We fix, not replace, wherever possible.",
  desc: "Many tradies quote a full new door because the markup is bigger. We'll always quote the smallest viable fix first — single panel swap, hinge replacement, track straightening — and only suggest a full replacement if the door is genuinely beyond repair. You see both options.",
  proof: "Repair-first quoting policy"
}, {
  number: "04",
  category: "Guarantee",
  title: "12-month written warranty on every job.",
  desc: "Workmanship + parts warranty in writing on every sectional repair. Hinges, rollers, panels, seals — all covered. Family business in Bass Hill since 2009, not a fly-by-night tradie subcontracting your job.",
  proof: "12mo workmanship + parts"
}];
const SOCIAL_PROOF = [{
  metric: "5.0★",
  label: "Google rated · 100+ reviews"
}, {
  metric: "2009",
  label: "Family-owned since"
}, {
  metric: "$0",
  label: "Callout fee"
}, {
  metric: "150+",
  label: "Sydney suburbs covered"
}];
export default function SectionalWhyUs() {
  return <section style={{
    background: CREAM,
    position: "relative",
    overflow: "hidden",
    padding: "120px 24px",
    fontFamily: FONT_BODY
  }}>
      <div aria-hidden style={{
      position: "absolute",
      top: 60,
      right: -40,
      fontFamily: FONT_HEAD,
      fontSize: "clamp(160px, 20vw, 300px)",
      fontWeight: 900,
      color: CREAM_DEEP,
      opacity: 0.55,
      letterSpacing: "-0.04em",
      lineHeight: 1,
      pointerEvents: "none",
      userSelect: "none",
      zIndex: 0
    }}>
        WHY US
      </div>

      <div aria-hidden style={{
      position: "absolute",
      top: 80,
      left: 24,
      width: 120,
      height: 1,
      background: GOLD,
      zIndex: 1
    }} />

      <div style={{
      position: "relative",
      zIndex: 2,
      maxWidth: 1200,
      margin: "0 auto"
    }}>
        {/* === HEADER === */}
        <div style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
        gap: 60,
        marginBottom: 80,
        alignItems: "end"
      }} className="sectional-why-header">
          <div>
            <div style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 20
          }}>
              <span style={{
              fontFamily: FONT_BODY,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: GOLD_DARK
            }}>
                05 / Why Sydney trusts Gold
              </span>
              <span style={{
              width: 32,
              height: 1,
              background: GOLD
            }} />
            </div>

            <h2 style={{
            fontFamily: FONT_HEAD,
            fontSize: "clamp(34px, 4.5vw, 56px)",
            fontWeight: 800,
            color: INK,
            lineHeight: 1.05,
            letterSpacing: "-0.025em",
            margin: 0
          }}>
              Why we{" "}
              <span style={{
              color: GOLD_DARK,
              fontStyle: "italic",
              fontWeight: 700
            }}>
                save the panel
              </span>
              <br />
              when others quote full replacement.
            </h2>
          </div>

          <p style={{
          fontFamily: FONT_BODY,
          fontSize: 17,
          color: INK_MUTED,
          lineHeight: 1.6,
          margin: 0,
          maxWidth: 460,
          justifySelf: "end"
        }}>
            Most sectional door problems don't need a new door — just the right
            panel, hinge, or seal. Four principles guide every job.
          </p>
        </div>

        {/* === 4 PILLARS === */}
        <div style={{
        display: "flex",
        flexDirection: "column",
        marginBottom: 80
      }}>
          {PILLARS.map((p, i) => <PillarRow key={i} pillar={p} isLast={i === PILLARS.length - 1} />)}
        </div>

        {/* === NAVY STAT BAR === */}
        <div style={{
        position: "relative",
        background: NAVY,
        borderRadius: 4,
        padding: "48px 48px",
        overflow: "hidden"
      }}>
          <div aria-hidden style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: 4,
          height: "100%",
          background: GOLD
        }} />

          <div aria-hidden style={{
          position: "absolute",
          top: "-50%",
          right: "-20%",
          width: 500,
          height: 500,
          background: `radial-gradient(circle, rgba(245,166,35,0.08) 0%, transparent 60%)`,
          pointerEvents: "none"
        }} />

          <div style={{
          position: "relative",
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) auto",
          gap: 48,
          alignItems: "center"
        }} className="sectional-why-stats-grid">
            <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 32
          }} className="sectional-why-stats">
              {SOCIAL_PROOF.map((s, i) => <div key={i} style={{
              borderLeft: i === 0 ? "none" : `1px solid rgba(255,255,255,0.1)`,
              paddingLeft: i === 0 ? 0 : 24
            }}>
                  <div style={{
                fontFamily: FONT_HEAD,
                fontSize: "clamp(28px, 3vw, 40px)",
                fontWeight: 800,
                color: GOLD,
                letterSpacing: "-0.025em",
                lineHeight: 1,
                marginBottom: 10
              }}>
                    {s.metric}
                  </div>
                  <div style={{
                fontFamily: FONT_BODY,
                fontSize: 13,
                color: "rgba(255,255,255,0.7)",
                lineHeight: 1.4,
                fontWeight: 500
              }}>
                    {s.label}
                  </div>
                </div>)}
            </div>

            <div style={{
            display: "flex",
            flexDirection: "column",
            gap: 10
          }}>
              <a href="tel:0485000933" style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              padding: "16px 24px",
              background: GOLD,
              color: NAVY,
              fontFamily: FONT_HEAD,
              fontSize: 15,
              fontWeight: 700,
              borderRadius: 8,
              textDecoration: "none",
              whiteSpace: "nowrap",
              transition: "transform 0.2s, box-shadow 0.2s",
              boxShadow: "0 6px 20px rgba(245,166,35,0.3)"
            }} onMouseEnter={e => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 10px 28px rgba(245,166,35,0.45)";
            }} onMouseLeave={e => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 6px 20px rgba(245,166,35,0.3)";
            }}>
                <Phone size={16} strokeWidth={2.5} />
                0485 000 933
              </a>
              <Link to="#contact-us" style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "14px 24px",
              background: "transparent",
              color: "#ffffff",
              fontFamily: FONT_HEAD,
              fontSize: 14,
              fontWeight: 600,
              borderRadius: 8,
              textDecoration: "none",
              border: "1px solid rgba(255,255,255,0.25)",
              whiteSpace: "nowrap",
              justifyContent: "center",
              transition: "background 0.2s, border-color 0.2s"
            }} onMouseEnter={e => {
              (e.currentTarget as HTMLAnchorElement).style.background = "rgba(255,255,255,0.08)";
              (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(255,255,255,0.5)";
            }} onMouseLeave={e => {
              (e.currentTarget as HTMLAnchorElement).style.background = "transparent";
              (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(255,255,255,0.25)";
            }}>
                Free quote
                <ArrowUpRight size={14} strokeWidth={2.5} />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Responsive */}
      <style>{`
        @media (max-width: 900px) {
          .sectional-why-header {
            grid-template-columns: 1fr !important;
            gap: 32px !important;
            align-items: start !important;
          }
          .sectional-why-stats-grid {
            grid-template-columns: 1fr !important;
            gap: 32px !important;
          }
          .sectional-why-stats {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 24px !important;
          }
          .sectional-why-stats > div {
            border-left: none !important;
            padding-left: 0 !important;
          }
        }
      `}</style>
    </section>;
}
function PillarRow({
  pillar,
  isLast
}: {
  pillar: Pillar;
  isLast: boolean;
}) {
  return <div className="sectional-pillar-row" style={{
    display: "grid",
    gridTemplateColumns: "120px minmax(0, 1.4fr) minmax(0, 1fr)",
    gap: 48,
    padding: "40px 0",
    borderBottom: isLast ? "none" : `1px solid ${CREAM_DEEP}`,
    alignItems: "start"
  }}>
      <div>
        <div style={{
        fontFamily: FONT_HEAD,
        fontSize: 48,
        fontWeight: 800,
        color: GOLD,
        letterSpacing: "-0.02em",
        lineHeight: 1,
        marginBottom: 8
      }}>
          {pillar.number}
        </div>
        <div style={{
        fontFamily: FONT_BODY,
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: "0.2em",
        textTransform: "uppercase",
        color: GOLD_DARK
      }}>
          {pillar.category}
        </div>
      </div>

      <div>
        <h3 style={{
        fontFamily: FONT_HEAD,
        fontSize: "clamp(22px, 2.2vw, 30px)",
        fontWeight: 700,
        color: INK,
        lineHeight: 1.2,
        letterSpacing: "-0.015em",
        margin: "0 0 14px"
      }}>
          {pillar.title}
        </h3>
        <p style={{
        fontFamily: FONT_BODY,
        fontSize: 16,
        color: INK_MUTED,
        lineHeight: 1.6,
        margin: 0
      }}>
          {pillar.desc}
        </p>
      </div>

      <div style={{
      padding: "20px 24px",
      background: "#ffffff",
      border: `1px solid ${CREAM_DEEP}`,
      borderLeft: `3px solid ${GOLD}`,
      borderRadius: 4
    }}>
        <div style={{
        fontFamily: FONT_BODY,
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: "0.2em",
        textTransform: "uppercase",
        color: GOLD_DARK,
        marginBottom: 8
      }}>
          The proof
        </div>
        <div style={{
        fontFamily: FONT_HEAD,
        fontSize: 17,
        fontWeight: 700,
        color: INK,
        lineHeight: 1.3,
        letterSpacing: "-0.01em"
      }}>
          {pillar.proof}
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .sectional-pillar-row {
            grid-template-columns: 1fr !important;
            gap: 20px !important;
            padding: 32px 0 !important;
          }
        }
      `}</style>
    </div>;
}